import React, { useEffect, useRef, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  TextField,
  Button,
  Badge,
  Spinner,
  Divider,
  LegacyCard,
  IndexTable,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatPrice, formatDate } from "../../utility/funcs";
import { applyAllocations } from "../../utility/payments";
import { invoiceDue } from "../../utility/invoicing";
import { UserAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

function ConciliationDashboard() {
  const navigate = useNavigate();
  const { session } = UserAuth();
  const [unallocatedPayments, setUnallocatedPayments] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [uninvoicedPayments, setUninvoicedPayments] = useState([]);
  const [uninvoicedRentals, setUninvoicedRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quick allocate state
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [allocateAmount, setAllocateAmount] = useState("");
  const [saving, setSaving] = useState(false);
  // Synchronous guard against fast double-clicks on Conciliar.
  const savingRef = useRef(false);

  const fetchData = async () => {
    setLoading(true);

    const [paymentsRes, invoicesRes, uninvoicedPaymentsRes, uninvoicedRentalsRes] = await Promise.all([
      supabase
        .from("transactions")
        .select("*, account(name), customer(first_name, last_name)")
        .gt("unallocated_amount", 0)
        .eq("type", "income")
        .eq("isDeposit", false)
        .order("created_at", { ascending: false }),
      supabase
        .from("invoices_issued")
        .select("*, customer:customers!customer_id(first_name, last_name), company:companies!company_id(name)")
        .gt("total", 0)
        .neq("status", "void")
        .order("date", { ascending: true }),
      supabase
        .from("transactions")
        .select("*, account(name)")
        .eq("type", "income")
        .eq("isDeposit", false)
        .eq("invoiced", false)
        .order("created_at", { ascending: false }),
      // Rentals without any invoice — corto plazo only, and only rentals that
      // have ended (car checked in or beyond). !inner forces the branch join to
      // filter parent rows by segment.
      supabase
        .from("reservations")
        .select("id, total_price, status, customer(first_name, last_name), car(plate, model(name)), company:companies!company(name), branch:branches!inner(segment)")
        .eq("type", "rental")
        .gt("total_price", 0)
        .in("status", ["active", "completed"])
        .eq("checked_in", true)
        .eq("branch.segment", "corto_plazo")
        .order("id", { ascending: false }),
    ]);

    if (paymentsRes.data) setUnallocatedPayments(paymentsRes.data);
    if (uninvoicedPaymentsRes.data) setUninvoicedPayments(uninvoicedPaymentsRes.data);

    // Calculate uninvoiced amount per reservation
    // Sum all invoice totals per reservation_id, net of notas de crédito
    if (uninvoicedRentalsRes.data && invoicesRes.data) {
      const invoicedPerReservation = {};
      (invoicesRes.data || []).forEach(inv => {
        if (inv.reservation_id && inv.status !== "void") {
          invoicedPerReservation[inv.reservation_id] = (invoicedPerReservation[inv.reservation_id] || 0) + (inv.total || 0) - (inv.amount_credited || 0);
        }
      });

      setUninvoicedRentals(
        uninvoicedRentalsRes.data
          .map(r => ({
            ...r,
            amount_invoiced: invoicedPerReservation[r.id] || 0,
            amount_uninvoiced: r.total_price - (invoicedPerReservation[r.id] || 0),
          }))
          .filter(r => r.amount_uninvoiced > 0.01)
      );
    }

    // Invoices that still owe something
    if (invoicesRes.data) {
      setUnpaidInvoices(
        invoicesRes.data.filter((inv) => invoiceDue(inv) > 0.01)
      );
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const quickAllocate = async () => {
    if (!selectedPayment || !selectedInvoice || !allocateAmount) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
    const amount = parseFloat(allocateAmount) || 0;
    if (amount <= 0) return;

    // Delegate ledger writes (allocation row, invoice amount_paid/status,
    // transaction unallocated_amount/invoiced) to the shared helper. DB
    // triggers handle reservations.paid/balance/fully_paid.
    await applyAllocations({
      transactionId: selectedPayment.id,
      transactionAmount: selectedPayment.amount,
      allocations: [{ invoice: selectedInvoice, amount }],
      createdBy: session?.user?.id,
    });

    // Alegra sync (payment / complemento de pago) is handled inside
    // applyAllocations now — single source of truth.

    setSelectedPayment(null);
    setSelectedInvoice(null);
    setAllocateAmount("");
    fetchData();
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const totalUnallocated = unallocatedPayments.reduce(
    (sum, p) => sum + (p.unallocated_amount || 0), 0
  );
  const totalUnpaid = unpaidInvoices.reduce(
    (sum, inv) => sum + invoiceDue(inv), 0
  );

  return (
    <AppWrapper>
      <Page title="Conciliación">
        {loading ? (
          <Spinner size="small" />
        ) : (
          <BlockStack gap="400">
            {/* Summary cards */}
            <InlineGrid columns={3} gap="300">
              <Card>
                <Text variant="bodySm" tone="subdued">Rentas sin facturar</Text>
                <Text variant="headingLg" tone="critical">
                  {formatPrice(uninvoicedRentals.reduce((s, r) => s + (r.amount_uninvoiced || 0), 0))}
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {uninvoicedRentals.length} rentas
                </Text>
              </Card>
              <Card>
                <Text variant="bodySm" tone="subdued">Pagos sin conciliar</Text>
                <Text variant="headingLg" tone="attention">
                  {formatPrice(totalUnallocated)}
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {unallocatedPayments.length} pagos
                </Text>
              </Card>
              <Card>
                <Text variant="bodySm" tone="subdued">Facturas por cobrar</Text>
                <Text variant="headingLg" tone="critical">
                  {formatPrice(totalUnpaid)}
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {unpaidInvoices.length} facturas
                </Text>
              </Card>
            </InlineGrid>

            {/* Quick allocate */}
            {selectedPayment && selectedInvoice && (
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingSm">Conciliación rápida</Text>
                  <InlineGrid columns="1fr 1fr 1fr auto" gap="200">
                    <BlockStack gap="050">
                      <Text variant="bodySm" fontWeight="semibold">Pago #{selectedPayment.id}</Text>
                      <Text variant="bodySm" tone="subdued">
                        Disponible: {formatPrice(selectedPayment.unallocated_amount)}
                      </Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text variant="bodySm" fontWeight="semibold">
                        {selectedInvoice.invoice_number || `#${selectedInvoice.alegra_id}`}
                      </Text>
                      <Text variant="bodySm" tone="subdued">
                        Debe: {formatPrice(invoiceDue(selectedInvoice))}
                      </Text>
                    </BlockStack>
                    <TextField
                      value={allocateAmount}
                      onChange={setAllocateAmount}
                      type="number"
                      prefix="$"
                      autoComplete="off"
                    />
                    <Button
                      variant="primary"
                      onClick={quickAllocate}
                      loading={saving}
                      disabled={!allocateAmount || saving}
                    >
                      Aplicar
                    </Button>
                  </InlineGrid>
                </BlockStack>
              </Card>
            )}

            <InlineGrid columns={2} gap="300">
              {/* Unallocated payments */}
              <LegacyCard>
                <div style={{ padding: "1rem 1rem 0.5rem" }}>
                  <Text variant="headingMd">Pagos sin conciliar</Text>
                </div>
                <IndexTable
                  resourceName={{ singular: "Pago", plural: "Pagos" }}
                  selectable={false}
                  itemCount={unallocatedPayments.length}
                  headings={[
                    { title: "#" },
                    { title: "Cliente" },
                    { title: "Res#" },
                    { title: "Cuenta" },
                    { title: "Total" },
                    { title: "Sin conciliar" },
                  ]}
                >
                  {unallocatedPayments.map((p, i) => {
                    const payerName = p.customer
                      ? `${p.customer.first_name || ""} ${p.customer.last_name || ""}`.trim()
                      : "";
                    return (
                    <IndexTable.Row
                      key={p.id}
                      id={p.id}
                      position={i}
                      selected={selectedPayment?.id === p.id}
                      onClick={() => {
                        setSelectedPayment(p);
                        const maxAmount = Math.min(
                          p.unallocated_amount || 0,
                          selectedInvoice
                            ? invoiceDue(selectedInvoice)
                            : p.unallocated_amount || 0
                        );
                        setAllocateAmount(String(Math.round(maxAmount * 100) / 100));
                      }}
                    >
                      <IndexTable.Cell>
                        <Text variant="bodySm">{p.id}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{payerName || "-"}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{p.reservation ? `#${p.reservation}` : "-"}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{p.account?.name || "-"}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {formatPrice(p.amount)}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text tone="attention" fontWeight="bold">
                          {formatPrice(p.unallocated_amount)}
                        </Text>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                    );
                  })}
                </IndexTable>
              </LegacyCard>

              {/* Unpaid invoices */}
              <LegacyCard>
                <div style={{ padding: "1rem 1rem 0.5rem" }}>
                  <Text variant="headingMd">Facturas por cobrar</Text>
                </div>
                <IndexTable
                  resourceName={{ singular: "Factura", plural: "Facturas" }}
                  selectable={false}
                  itemCount={unpaidInvoices.length}
                  headings={[
                    { title: "Folio" },
                    { title: "Cliente" },
                    { title: "Res#" },
                    { title: "Fecha" },
                    { title: "Total" },
                    { title: "Debe" },
                  ]}
                >
                  {unpaidInvoices.map((inv, i) => {
                    const due = invoiceDue(inv);
                    const recipient =
                      inv.company?.name ||
                      (inv.customer
                        ? `${inv.customer.first_name || ""} ${inv.customer.last_name || ""}`.trim()
                        : "");
                    return (
                      <IndexTable.Row
                        key={inv.id}
                        id={inv.id}
                        position={i}
                        selected={selectedInvoice?.id === inv.id}
                        onClick={() => {
                          setSelectedInvoice(inv);
                          const maxAmount = Math.min(
                            due,
                            selectedPayment
                              ? selectedPayment.unallocated_amount || 0
                              : due
                          );
                          setAllocateAmount(String(Math.round(maxAmount * 100) / 100));
                        }}
                      >
                        <IndexTable.Cell>
                          <Text variant="bodySm" fontWeight="semibold">
                            {inv.invoice_number || `#${inv.alegra_id}`}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text variant="bodySm">{recipient || "-"}</Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text variant="bodySm">{inv.reservation_id ? `#${inv.reservation_id}` : "-"}</Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {formatDate(inv.date)}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {formatPrice(inv.total)}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text tone="critical" fontWeight="bold">
                            {formatPrice(due)}
                          </Text>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    );
                  })}
                </IndexTable>
              </LegacyCard>
            </InlineGrid>

            {/* Uninvoiced rentals */}
            {uninvoicedRentals.length > 0 && (
              <LegacyCard>
                <div style={{ padding: "1rem 1rem 0.5rem" }}>
                  <Text variant="headingMd">Rentas sin facturar</Text>
                </div>
                <IndexTable
                  resourceName={{ singular: "Renta", plural: "Rentas" }}
                  selectable={false}
                  itemCount={uninvoicedRentals.length}
                  headings={[
                    { title: "#" },
                    { title: "Cliente / Empresa" },
                    { title: "Auto" },
                    { title: "Total" },
                    { title: "Facturado" },
                    { title: "Sin facturar" },
                  ]}
                >
                  {uninvoicedRentals.map((r, i) => (
                    <IndexTable.Row
                      key={r.id}
                      id={r.id}
                      position={i}
                      onClick={() => navigate(`/reservation?id=${r.id}`)}
                    >
                      <IndexTable.Cell>
                        <Text variant="bodySm" fontWeight="semibold">#{r.id}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">
                          {r.company?.name || `${r.customer?.first_name || ""} ${r.customer?.last_name || ""}`}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">
                          {r.car?.model?.name || ""} ({r.car?.plate || ""})
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {formatPrice(r.total_price)}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {r.amount_invoiced > 0 ? (
                          <Text variant="bodySm" tone="success">{formatPrice(r.amount_invoiced)}</Text>
                        ) : (
                          <Text variant="bodySm" tone="subdued">$0</Text>
                        )}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm" tone="critical" fontWeight="bold">
                          {formatPrice(r.amount_uninvoiced)}
                        </Text>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              </LegacyCard>
            )}
          </BlockStack>
        )}
      </Page>
    </AppWrapper>
  );
}

export default ConciliationDashboard;
